<template>
  <button
    type="button"
    class="btn btn-link btn-sm text-muted text-decoration-none d-inline-flex align-items-center gap-2 p-0"
    :disabled="is_button_disabled"
    :title="button_title"
    @click="open_modal"
  >
    <i class="bi bi-pencil-square" aria-hidden="true"></i>
    <span>Edit</span>
  </button>

  <div
    v-if="is_modal_open"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @click.self="close_modal"
  >
    <div class="modal-dialog modal-dialog-centered" @click.stop>
      <div class="modal-content border-0 shadow rounded-4 overflow-hidden">
        <div class="modal-header border-bottom px-4 py-4 align-items-start position-relative">
          <div>
            <h3 class="h5 fw-bold mb-2">Edit name</h3>
            <p class="text-muted mb-0">Update the applicant or entity name for this onboarding record.</p>
          </div>
          <button
            type="button"
            class="btn btn-link text-muted p-0 position-absolute top-0 end-0 mt-4 me-4"
            aria-label="Close edit name modal"
            @click="close_modal"
          >
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>

        <div class="modal-body px-4 py-4 bg-white">
          <div v-if="error_message" class="alert alert-warning py-2 mb-3">
            {{ error_message }}
          </div>

          <label class="form-label text-uppercase text-muted fw-bold small">Name</label>
          <input
            v-model="draft_name"
            type="text"
            class="form-control"
            placeholder="Enter name"
            maxlength="255"
            @keyup.enter="save_name"
          >
        </div>

        <div class="modal-footer border-top px-4 py-3 bg-white d-flex justify-content-end gap-3">
          <button type="button" class="btn btn-link text-muted fw-semibold text-decoration-none" @click="close_modal">
            Cancel
          </button>
          <button
            type="button"
            class="btn text-white px-4 py-2 border-0 onboarding-edit-update-button"
            :disabled="is_saving"
            @click="save_name"
          >
            <span>{{ is_saving ? 'Updating...' : 'Update' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="is_modal_open" class="modal-backdrop fade show"></div>
</template>

<script>
/* eslint-disable */
import { update_entity_name } from '@/services/onboarding_entity_name_service';
import alert_mixin from '@/utils/mixins/alert_mixin';

/** @typedef {Record<string, unknown>} LooseRecord */
/**
 * @typedef {{
 *   entityRecord: LooseRecord,
 *   currentName: string,
 *   resolved_entity_id: number,
 *   resolved_name: string,
 *   is_modal_open: boolean,
 *   is_saving: boolean,
 *   error_message: string,
 *   draft_name: string,
 *   show_error: (message: string) => void,
 *   show_success: (message: string) => void,
 *   $emit: (event: string, payload: unknown) => void
 * }} EntityNameEditButtonVm
 */

/**
 * @param {unknown[]} values
 * @returns {number}
 */
function first_valid_number(values = []) {
  for (const value of values) {
    const normalized_value = Number(value || 0);
    if (normalized_value > 0) return normalized_value;
  }

  return 0;
}

/**
 * @param {unknown} value
 * @returns {LooseRecord | null}
 */
function as_entity_record(value) {
  if (!value || typeof value !== 'object') return null;
  return /** @type {LooseRecord} */ (value);
}

/**
 * @param {unknown} value
 * @returns {LooseRecord | null}
 */
function as_id_record(value) {
  if (!value || typeof value !== 'object') return null;
  return /** @type {LooseRecord} */ (value);
}

/**
 * @param {LooseRecord | null | undefined} record
 * @returns {number}
 */
function resolve_entity_id(record) {
  const normalized_record = as_entity_record(record);
  const case_item = as_entity_record(normalized_record?.case_item);
  const profile = as_id_record(normalized_record?.profile);
  const entity = as_id_record(normalized_record?.entity);
  const case_profile = as_id_record(case_item?.profile);
  const selected_case = as_entity_record(normalized_record?.selected_case);

  return first_valid_number([
    normalized_record?.entity_profile_id,
    normalized_record?.entity_id,
    profile?.id,
    entity?.id,
    case_item?.entity_profile_id,
    case_item?.entity_id,
    case_profile?.id,
    selected_case?.entity_profile_id
  ]);
}

/**
 * @param {string} current_name
 * @param {LooseRecord | null | undefined} record
 * @returns {string}
 */
function resolve_name(current_name = '', record) {
  const normalized_record = as_entity_record(record);
  const case_item = as_entity_record(normalized_record?.case_item);

  return String(
    current_name
    || normalized_record?.name
    || normalized_record?.case_name
    || normalized_record?.title
    || case_item?.name
    || ''
  ).trim();
}

/**
 * @param {EntityNameEditButtonVm} vm
 * @returns {void}
 */
function reset_modal_state(vm) {
  vm.is_modal_open = false;
  vm.error_message = '';
  vm.draft_name = '';
}

/**
 * @param {EntityNameEditButtonVm} vm
 * @param {string} message
 * @returns {void}
 */
function set_error(vm, message) {
  vm.error_message = message;
}

/**
 * @param {EntityNameEditButtonVm} vm
 * @param {string} next_name
 * @returns {Promise<LooseRecord>}
 */
async function request_name_update(vm, next_name) {
  const response = await update_entity_name({
    entity_id: vm.resolved_entity_id,
    name: next_name
  });
  return /** @type {LooseRecord} */ (response || {});
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function resolve_error_message(error) {
  if (error && typeof error === 'object') {
    const response = /** @type {{ response?: { data?: { message?: string } }, message?: string }} */ (error);
    return response.response?.data?.message || response.message || 'Unable to update the name.';
  }

  return 'Unable to update the name.';
}

export default {
  name: 'EntityNameEditButton',
  mixins: [alert_mixin],
  props: {
    entityRecord: {
      type: Object,
      default: () => ({})
    },
    currentName: {
      type: String,
      default: ''
    }
  },
  emits: ['updated'],
  data: () => ({
    is_modal_open: false,
    is_saving: false,
    error_message: '',
    draft_name: ''
  }),
  computed: {
    /** @this {EntityNameEditButtonVm} */
    resolved_entity_id() {
      return resolve_entity_id(this.entityRecord);
    },
    /** @this {EntityNameEditButtonVm} */
    resolved_name() {
      return resolve_name(this.currentName, this.entityRecord);
    },
    /** @this {EntityNameEditButtonVm} */
    is_button_disabled() {
      return !this.resolved_entity_id;
    },
    /** @this {EntityNameEditButtonVm} */
    button_title() {
      return this.resolved_entity_id
        ? 'Edit name'
        : 'This drawer is not linked to a live entity record yet.';
    }
  },
  methods: {
    /** @this {EntityNameEditButtonVm} */
    open_modal() {
      if (!this.resolved_entity_id) return;
      set_error(this, '');
      this.draft_name = this.resolved_name;
      this.is_modal_open = true;
    },
    /** @this {EntityNameEditButtonVm} */
    close_modal() {
      if (this.is_saving) return;
      reset_modal_state(this);
    },
    /** @this {EntityNameEditButtonVm} */
    async save_name() {
      if (this.is_saving) return;

      const next_name = String(this.draft_name || '').trim();
      if (!next_name) {
        set_error(this, 'Name is required.');
        return;
      }

      this.is_saving = true;
      set_error(this, '');
      window.dispatchEvent(new CustomEvent('system-loader:show', {
        detail: 'Updating name...'
      }));

      try {
        const response = await request_name_update(this, next_name);

        if (response.status === 'error') {
          set_error(this, response.message || 'Unable to update the name.');
          this.show_error(this.error_message);
          return;
        }

        const payload = {
          entity_id: this.resolved_entity_id,
          name: String(response.data?.name || next_name).trim(),
          response
        };

        this.$emit('updated', payload);
        window.dispatchEvent(new CustomEvent('onboarding-entity-name-updated', {
          detail: payload
        }));
        reset_modal_state(this);
        this.show_success(response.message || 'Entity name updated successfully.');
        window.setTimeout(() => {
          window.location.reload();
        }, 900);
      } catch (error) {
        set_error(this, resolve_error_message(error));
      } finally {
        this.is_saving = false;
        window.dispatchEvent(new CustomEvent('system-loader:hide'));
      }
    }
  }
};
</script>
